import React, { useEffect, useRef, useState } from "react";
import { Input, Button, message, Space } from "antd";
import {
    AudioOutlined,
    StopOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import serverProxy from "cvat-core/src/server-proxy";


const mockTranscribe = async (
    blob: Blob,
): Promise<string> => {
    console.log("Transcribing audio:", blob);
    const results = [
        "This is a mocked transcription result.",
        "User is testing voice input.",
        "Speech recognition completed successfully.",
        "Voice recording processed.",
        "System ready for real ASR integration.",
    ];
    await new Promise((r) => setTimeout(r, 1200));
    const index = Math.floor(Math.random() * results.length);
    return results[index];
};

type VoiceValue = {
    audioUrl?: string;
    transcript?: string;
    editedText?: string;
};

type VoiceInputProps = {
    value?: VoiceValue;
    onChange?: (value: VoiceValue | undefined) => void;

    transcribe: (audioBlob: Blob) => Promise<string>;
    uploadAudio?: (audioBlob: Blob) => Promise<string>;

    streamWhileRecording?: boolean;
    streamingRequestMode?: "chunk" | "cumulative";
    appendMode?: boolean;
    placeholder?: string;
    disabled?: boolean;
    onRecordingStart?: () => void;
    onRecordingStop?: () => void;
};

const CHUNK_TIMESLICE_MS = 1800;
const { TextArea } = Input;

function getPreferredMimeType(): string | undefined {
    if (typeof MediaRecorder === "undefined") {
        return undefined;
    }

    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function mergeText(base: string, delta: string): string {
    const trimmedBase = (base || "").trim();
    const trimmedDelta = (delta || "").trim();

    if (!trimmedDelta) {
        return trimmedBase;
    }

    if (!trimmedBase) {
        return trimmedDelta;
    }

    return `${trimmedBase} ${trimmedDelta}`.replace(/\s+/g, " ").trim();
}

const VoiceInput: React.FC<VoiceInputProps> = ({
    value,
    onChange,
    transcribe,
    uploadAudio,
    streamWhileRecording = true,
    streamingRequestMode = "chunk",
    appendMode = true,
    placeholder,
    disabled = false,
    onRecordingStart,
    onRecordingStop,
}) => {
    // Use custom uploadAudio if provided, otherwise use the backend service
    const uploadAudioFn = serverProxy.server.uploadAudio;
    const valueRef = useRef(value);
    valueRef.current = value;
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const pendingChunkRef = useRef<Promise<void>>(Promise.resolve());
    const currentTextRef = useRef(value?.editedText ?? "");
    const currentValueRef = useRef<VoiceValue | undefined>(value);
    const chunkErrorRef = useRef(false);

    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);

    const editedText = value?.editedText ?? "";

    useEffect(() => {
        currentTextRef.current = value?.editedText ?? "";
        currentValueRef.current = value;
    }, [value]);

    const pushChunk = async (chunk: Blob): Promise<void> => {
        const delta = (await transcribe(chunk) || "").trim();
        if (!delta) {
            return;
        }

        const nextText = appendMode ? mergeText(currentTextRef.current, delta) : delta;
        currentTextRef.current = nextText;
        const prev = currentValueRef.current;
        const nextValue: VoiceValue = {
            ...prev,
            transcript: appendMode ? mergeText(prev?.transcript ?? "", delta) : delta,
            editedText: nextText,
        };
        currentValueRef.current = nextValue;
        onChange?.(nextValue);
    };

    const startRecording = async () => {
        if (recording || processing) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            streamRef.current = stream;

            const mimeType = getPreferredMimeType();
            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            chunksRef.current = [];
            pendingChunkRef.current = Promise.resolve();
            chunkErrorRef.current = false;

            recorder.ondataavailable = (e) => {
                if (!e.data || !e.data.size) {
                    return;
                }

                const chunk = e.data;
                chunksRef.current.push(chunk);

                if (streamWhileRecording) {
                    const streamingPayload = streamingRequestMode === "cumulative"
                        ? new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" })
                        : chunk;
                    pendingChunkRef.current = pendingChunkRef.current
                        .then(async () => pushChunk(streamingPayload))
                        .catch(() => {
                            if (!chunkErrorRef.current) {
                                chunkErrorRef.current = true;
                                message.error("Failed to transcribe one of the audio chunks");
                            }
                        });
                }
            };

            recorder.onerror = () => {
                message.error("Voice recorder failed unexpectedly");
            };

            if (streamWhileRecording) {
                recorder.start(CHUNK_TIMESLICE_MS);
            } else {
                recorder.start();
            }
            mediaRecorderRef.current = recorder;
            setRecording(true);
            onRecordingStart?.();
        } catch {
            message.error("Cannot access microphone");
        }
    };

    const stopRecording = async () => {
        const recorder = mediaRecorderRef.current;
        if (!recorder) {
            return;
        }

        setRecording(false);
        setProcessing(true);

        try {
            await new Promise<void>((resolve) => {
                let settled = false;
                const finalize = () => {
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                };

                recorder.onstop = () => finalize();

                try {
                    if (recorder.state !== "inactive") {
                        recorder.stop();
                    } else {
                        finalize();
                    }
                } catch {
                    finalize();
                }

                // Ensure the UI never gets stuck in loading state if onstop does not fire.
                window.setTimeout(finalize, 1500);
            });

            await pendingChunkRef.current;

            if (!streamWhileRecording && chunksRef.current.length) {
                const fullBlob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
                const delta = (await transcribe(fullBlob) || "").trim();
                if (delta) {
                    const nextText = appendMode ? mergeText(currentTextRef.current, delta) : delta;
                    currentTextRef.current = nextText;
                    const prev = currentValueRef.current;
                    const nextValue: VoiceValue = {
                        ...prev,
                        transcript: appendMode ? mergeText(prev?.transcript ?? "", delta) : delta,
                        editedText: nextText,
                    };
                    currentValueRef.current = nextValue;
                    onChange?.(nextValue);
                }
            }

            if (uploadAudio && chunksRef.current.length) {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
                const audioUrl = await uploadAudio(blob);
                const prev = currentValueRef.current;
                const nextValue: VoiceValue = {
                    ...prev,
                    audioUrl,
                    editedText: currentTextRef.current,
                };
                currentValueRef.current = nextValue;
                onChange?.(nextValue);
            }
        } catch {
            message.error("Failed to process audio");
        } finally {
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
            setProcessing(false);
            onRecordingStop?.();
        }
    };

    const handleTextChange = (text: string) => {
        currentTextRef.current = text;
        const prev = currentValueRef.current;
        const nextValue: VoiceValue = {
            ...prev,
            editedText: text,
        };
        currentValueRef.current = nextValue;
        onChange?.(nextValue);
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", width: "100%" }}>
                <TextArea
                    value={editedText}
                    disabled={disabled || recording}
                    placeholder={placeholder}
                    autoSize={{ minRows: 2, maxRows: 10 }}
                    style={{ resize: "none" }}
                    onChange={(e) => handleTextChange(e.target.value)}
                />
                <Button
                    type={recording ? "primary" : "default"}
                    icon={processing ? <LoadingOutlined /> : recording ? <StopOutlined /> : <AudioOutlined />}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={disabled || processing}
                />
            </div>
        </Space>
    );
};

export default VoiceInput;

export { VoiceInput, mockTranscribe };
